<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import type { AgentScopeOption } from '../types'

const props = defineProps<{
  open: boolean
  title: string
  eyebrow: string
  searchLabel: string
  searchPlaceholder: string
  emptyMessage: string
  items: AgentScopeOption[]
}>()

const emit = defineEmits<{
  close: []
  select: [item: AgentScopeOption]
}>()

const search = ref('')
const page = ref(1)
const pageSize = 8
const filteredItems = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return props.items
  return props.items.filter((item) =>
    [item.name, ...item.roles]
      .some((value) => String(value ?? '').toLocaleLowerCase().includes(query)),
  )
})
const totalPages = computed(() => Math.max(1, Math.ceil(filteredItems.value.length / pageSize)))
const visibleItems = computed(() => {
  const start = (page.value - 1) * pageSize
  return filteredItems.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleItems.value.map((item) => ({
    id: item.id,
    title: item.name,
    subtitle: item.roles.join(', ') || 'Agent',
    meta: `#${item.id}`,
  })),
)

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  page.value = 1
})

function selectOption(option: SmartPickerOption): void {
  const item = visibleItems.value.find((entry) => entry.id === Number(option.id))
  if (item) emit('select', item)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    :eyebrow="eyebrow"
    :search-label="searchLabel"
    :search-placeholder="searchPlaceholder"
    :search-value="search"
    :loading="false"
    error-message=""
    :empty-message="emptyMessage"
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="`${filteredItems.length} items · Page ${page} of ${totalPages}`"
    close-aria-label="Close picker"
    retry-label="Retry"
    previous-label="Previous"
    next-label="Next"
    cancel-label="Cancel"
    @close="emit('close')"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  />
</template>
